import React, { useState, useEffect, useRef, useCallback } from 'react';
import ClipAPI, { ClipArchiveFilter, ClipSummary } from '../../api/clip';
import { constructMediaUrl } from '../../api/helpers';
import { Button } from '../ui';

interface ClipsBrowserProps {
  /** Called after a clip is hydrated and loaded into the ViewModel */
  onClipLoaded?: () => void;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const LIMIT = 20;

const FILTERS: { value: ClipArchiveFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'rendered', label: 'Rendered' },
  { value: 'unfinished', label: 'Unfinished' },
];

// ==================== Archived Clip Card ====================

interface ArchivedClipCardProps {
  clip: ClipSummary;
  isHydrating: boolean;
  onLoad: (clip: ClipSummary) => void;
}

const ArchivedClipCard: React.FC<ArchivedClipCardProps> = ({ clip, isHydrating, onLoad }) => {
  const { images, ai_videos, audios } = clip.media_count;
  const isRendered = Boolean(clip.finished_at);

  return (
    <div className="archive-clip-card">
      {/* Thumbnail */}
      <div className="archive-clip-thumb">
        {clip.thumbnail_url ? (
          <img
            src={constructMediaUrl(clip.thumbnail_url)}
            alt={clip.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span style={{ color: 'var(--text-dim, #444)', fontSize: '24px' }}>▶</span>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <p className="archive-clip-name">
            {clip.name || '(unnamed)'}
          </p>
          <span
            className={`archive-clip-badge ${isRendered ? 'badge-rendered' : 'badge-unfinished'}`}
            data-testid={isRendered ? 'badge-rendered' : 'badge-unfinished'}
          >
            {isRendered ? 'Rendered' : 'Unfinished'}
          </span>
          {clip.is_dirty && (
            <span className="archive-clip-badge badge-dirty">
              Needs re-render
            </span>
          )}
        </div>

        <p className="archive-clip-date">
          {formatDate(clip.finished_at ?? clip.updated_at)}
        </p>

        {/* Media count chips */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
          {images > 0 && (
            <span className="archive-clip-badge badge-images">
              {images} img
            </span>
          )}
          {ai_videos > 0 && (
            <span className="archive-clip-badge badge-videos">
              {ai_videos} vid
            </span>
          )}
          {audios > 0 && (
            <span className="archive-clip-badge badge-audios">
              {audios} aud
            </span>
          )}
          {images === 0 && ai_videos === 0 && audios === 0 && (
            <span style={{ fontSize: '10px', color: 'var(--text-dim, #444)' }}>no media</span>
          )}
        </div>
      </div>

      {/* Load button */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {isHydrating ? (
          <span className="archive-clip-loading">Loading…</span>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => onLoad(clip)}>
            Load
          </Button>
        )}
      </div>
    </div>
  );
};

// ==================== Main Component ====================

const ClipsBrowser: React.FC<ClipsBrowserProps> = ({ onClipLoaded }) => {
  const [archivedClips, setArchivedClips] = useState<ClipSummary[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hydratingId, setHydratingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filter, setFilter] = useState<ClipArchiveFilter>('all');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const loadArchivedClips = useCallback(
    async (p: number, q: string = search, f: ClipArchiveFilter = filter) => {
      setLoading(true);
      setError(null);
      try {
        const result = await ClipAPI.listArchivedClips(p, LIMIT, q, f);
        setArchivedClips(result.items);
        setTotal(result.total);
        setPage(p);
      } catch {
        setError('Failed to load archived clips.');
      } finally {
        setLoading(false);
      }
    },
    [search, filter]
  );

  // Initial load
  useEffect(() => {
    loadArchivedClips(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      loadArchivedClips(1, value);
    }, 300);
  };

  const handleFilterChange = (next: ClipArchiveFilter) => {
    if (next === filter) return;
    setFilter(next);
    loadArchivedClips(1, search, next);
  };

  const handleOpenArchived = async (clip: ClipSummary) => {
    setHydratingId(clip.id);
    setError(null);
    try {
      await ClipAPI.hydrateClip(clip.id);
      // Clip is now loaded in the ViewModel — notify parent to refresh active clips
      onClipLoaded?.();
    } catch {
      setError(`Failed to load clip: ${clip.name || clip.id}`);
    } finally {
      setHydratingId(null);
    }
  };

  return (
    <div className="archive-page">
      <div className="archive-header">
        <h2 className="section-title">Clip Archive</h2>
        <span className="archive-total-badge">{total} total</span>
      </div>

      {/* Search bar */}
      <div className="archive-search-row">
        <input
          type="text"
          className="archive-search-input"
          placeholder="Search by name…"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchInput && (
          <button
            type="button"
            className="archive-search-clear"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              loadArchivedClips(1, '');
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Render-state filter */}
      <div className="archive-filter-row" role="group" aria-label="Filter clips by render state">
        {FILTERS.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={filter === option.value ? 'secondary' : 'ghost'}
            aria-pressed={filter === option.value}
            onClick={() => handleFilterChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <p className="archive-error">{error}</p>
      )}

      {/* Clip list */}
      {loading ? (
        <div className="archive-empty-state">Loading archived clips…</div>
      ) : archivedClips.length === 0 ? (
        <div className="archive-empty-state">
          {search ? `No clips matching "${search}".` : 'No clips found.'}
        </div>
      ) : (
        <div className="archive-clip-list">
          {archivedClips.map((clip) => (
            <ArchivedClipCard
              key={clip.id}
              clip={clip}
              isHydrating={hydratingId === clip.id}
              onLoad={handleOpenArchived}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="archive-pagination">
          <span className="archive-pagination-info">
            Page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => loadArchivedClips(page - 1)}
              disabled={page <= 1 || loading}
            >
              ← Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => loadArchivedClips(page + 1)}
              disabled={page >= totalPages || loading}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClipsBrowser;
